<script setup lang="ts">
import { cn } from '@/utils';
import {
	imageWrapperVariant,
	itemTitleVariant,
	linkListVariant,
	type LinkClickedWithCategory,
	type LinkListItem,
	type LinkListProps
} from '.';

const props = withDefaults(defineProps<LinkListProps>(), {
	title: ''
});

const emit = defineEmits<{
	(e: 'link-category-clicked', link: LinkListItem): void;
	(e: 'link-clicked', link: LinkClickedWithCategory): void;
}>();

const hasImage = computed(() => props.links.some(link => link.image));
</script>

<template>
	<section :class="cn(linkListVariant({ variant }), props.class)">
		<p
			:class="
				cn(
					'md:text-[32px]) text-left font-matter-sq text-2xl font-normal leading-normal tracking-[-0.48px] text-primary',
					titleClass
				)
			"
		>
			{{ title }}
		</p>
		<div
			v-if="hasImage"
			:class="cn(imageWrapperVariant({ variant }), imageWrapperClass)"
		>
			<NuxtLink
				v-for="link in links"
				:key="`main-link-${link.name}`"
				:to="link.route"
				class="w-full"
				@click="emit('link-category-clicked', link)"
			>
				<div
					v-if="link.image"
					:class="
						cn(
							'relative flex h-16 w-full items-center justify-center bg-cover bg-center before:absolute before:inset-0 before:size-full before:bg-[linear-gradient(0deg,rgba(0,0,0,0.40)_0%,rgba(0,0,0,0.40)_100%)] md:h-[76px]',
							imageClass
						)
					"
					:style="`background-image: url(${link.image});`"
				>
					<h5 :class="cn(itemTitleVariant({ variant }), itemTitleClass)">
						{{ link.name }}
					</h5>
				</div>
			</NuxtLink>
		</div>
		<div
			:class="cn('flex flex-col gap-8 md:grid md:grid-cols-4', linkListClass)"
		>
			<div
				v-for="link in links"
				:key="`links-${link.name}`"
				:class="cn('flex flex-col gap-4 text-left md:pb-2', linkCategoryClass)"
			>
				<p
					v-if="!link.image"
					:class="[
						cn(itemTitleVariant({ variant }), itemTitleClass),
						{ hidden: !variant || variant === 'image' }
					]"
				>
					{{ link.name }}
				</p>
				<NuxtLink
					v-for="regionLink in link.links"
					:key="`region-link-${regionLink.label}`"
					:to="regionLink.route"
					:class="
						cn(
							'cursor-pointer text-base font-normal leading-normal text-primary underline decoration-white-dark-4 underline-offset-[6px] hover:decoration-primary disabled:text-primary/40 disabled:decoration-white-dark-4',
							linkClass
						)
					"
					@click="emit('link-clicked', { category: link.name, ...regionLink })"
				>
					{{ regionLink.label }}
				</NuxtLink>
			</div>
		</div>
	</section>
</template>
